import RecordView from "@/components/record/RecordView";
import { Clock2Icon, PercentIcon } from "lucide-react";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UseQueryResult } from "@tanstack/react-query";
import { ReadProjectSettlements } from "@/schemas/projectSettlements";
import { getNumberFormatter } from "@/lib/utils/getNumberFormatter";
import { getRenderer } from "@/modules/projects/components/settlementsDetails/utils/getRenderer";

interface PlannedDataSectionComponentProps {
  query: UseQueryResult<ReadProjectSettlements>;
}

const PlannedDataSectionComponent = ({
  query,
}: PlannedDataSectionComponentProps) => {
  const { i18n } = useTranslation();

  const numberFormatter = useMemo(
    () => getNumberFormatter(i18n.language),
    [i18n.language],
  );

  const rendererWithIcon = useMemo(
    () => getRenderer(numberFormatter),
    [numberFormatter],
  );

  return (
    <RecordView
      isOpen={true}
      query={query}
      translationKey={"projectSettlements"}
      header={() => i18n.t("projectSettlements.sections.plannedData.header")}
    >
      <RecordView.Field
        name="planned_hours"
        renderer={rendererWithIcon(<Clock2Icon className="w-4 h-4" />)}
      />
      <RecordView.Field name="planned_avg_rate" renderer={numberFormatter} />
      <RecordView.Field name="planned_income" renderer={numberFormatter} />
      <RecordView.Field
        name="scheduled_hours"
        renderer={rendererWithIcon(<Clock2Icon className="w-4 h-4" />)}
      />
      <RecordView.Field name="scheduled_avg_rate" renderer={numberFormatter} />
      <RecordView.Field name="scheduled_payment" renderer={numberFormatter} />
      <RecordView.Field
        name="planned_profitability"
        renderer={rendererWithIcon(<PercentIcon className="w-4 h-4" />)}
      />
    </RecordView>
  );
};

export const PlannedDataSection = memo(
  PlannedDataSectionComponent,
  (prevProps, nextProps) => {
    return prevProps.query.data?.id === nextProps.query.data?.id;
  },
);
